import { useEffect, useRef } from 'react';
import { createTraccarSocket } from '@/api/socket';
import { useFleetStore } from '@/stores/fleet';
import type { SocketMessage } from '@/api/types';

let singleton: ReturnType<typeof createTraccarSocket> | null = null;
let subscribers = 0;
let mainUnsubscribe: (() => void) | null = null;

function ensureConnected() {
  if (subscribers === 0) {
    singleton = createTraccarSocket();
    singleton.connect();
    // Atualiza o fleet store automaticamente
    mainUnsubscribe = singleton.subscribe((message) => {
      const { upsertDevices, upsertPositions } = useFleetStore.getState();
      if (message.devices?.length) upsertDevices(message.devices);
      if (message.positions?.length) upsertPositions(message.positions);
    });
  }
  subscribers += 1;
}

function release() {
  subscribers -= 1;
  if (subscribers === 0 && singleton) {
    mainUnsubscribe?.();
    mainUnsubscribe = null;
    singleton.disconnect();
    singleton = null;
  }
}

/**
 * Conecta o WebSocket único da app. Chame em algum componente de layout
 * sempre montado (AppLayout). Atualiza o fleet store automaticamente.
 */
export function useTraccarSocket() {
  useEffect(() => {
    ensureConnected();
    return () => release();
  }, []);
}

/**
 * Subscreve no mesmo socket singleton para receber TODAS as mensagens cruas
 * (devices, positions, events). Use para reagir a eventos específicos sem
 * mexer no store. O callback é guardado em ref pra não reconectar a cada
 * render se você passar inline.
 */
export function useTraccarMessage(callback: (msg: SocketMessage) => void) {
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  });

  useEffect(() => {
    ensureConnected();
    const unsub = singleton!.subscribe((msg) => callbackRef.current(msg));
    return () => {
      unsub();
      release();
    };
  }, []);
}
